/**
 * ⚠️  INTERNAL — /admin/controllers
 * Full catalog editor: visual grid, individual editor, bulk pricing, batch adjustments.
 */
import React, { useState, useCallback, useRef, DragEvent } from 'react';
import {
  AdminLoginGate, isAdminAuthed, clearAdminAuth,
} from '../../components/AdminLoginGate';
import SEOHead from '../../components/SEOHead';
import {
  useControllers,
  updateController,
  createController,
  deleteController,
  uploadControllerMedia,
  updateControllerMedia,
  deleteControllerMedia,
  bulkUpdatePrices,
  reorderControllers,
} from '../../lib/controllers-client';
import type { ControllerProduct, ControllerMedia } from '../../lib/controllers-client';

// ── Icons (inline SVG, same pattern used elsewhere in admin pages) ─────────────
const Icon = {
  Plus:   () => <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4"/></svg>,
  Edit:   () => <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536M4 16.5V20h3.5l10.06-10.06-3.5-3.5L4 16.5z"/></svg>,
  Trash:  () => <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2M10 11v6M14 11v6M5 7l1 13a1 1 0 001 1h10a1 1 0 001-1l1-13"/></svg>,
  Grip:   () => <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><circle cx="9" cy="6" r="1.5"/><circle cx="9" cy="12" r="1.5"/><circle cx="9" cy="18" r="1.5"/><circle cx="15" cy="6" r="1.5"/><circle cx="15" cy="12" r="1.5"/><circle cx="15" cy="18" r="1.5"/></svg>,
  X:      () => <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>,
  Star:   () => <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>,
  Upload: () => <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M16 12l-4-4-4 4M12 8v8"/></svg>,
  Refresh:() => <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>,
  Check:  () => <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>,
};

const STATUS_BADGE: Record<string, string> = {
  active:  'bg-green-500/20 text-green-400 border border-green-500/30',
  draft:   'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30',
  hidden:  'bg-gray-600/40 text-gray-400 border border-gray-600',
};

const TYPE_LABEL: Record<string, string> = {
  custom: 'Custom Graphics', standard: 'Standard Colors', edge: 'DualSense Edge',
};

// ── Media Manager (inside editor) ─────────────────────────────────────────────
function MediaManager({
  product,
  onChanged,
  onExpired,
}: {
  product: ControllerProduct;
  onChanged: () => void;
  onExpired: () => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [err, setErr]   = useState<string | null>(null);

  const media = [...(product.media ?? [])].sort((a, b) => {
    if (a.is_primary && !b.is_primary) return -1;
    if (!a.is_primary && b.is_primary) return 1;
    return a.sort_order - b.sort_order;
  });

  const handleUpload = async (file: File) => {
    setErr(null);
    setUploading(true);
    setUploadPct(0);
    try {
      await uploadControllerMedia(product.id, file, setUploadPct);
      onChanged();
    } catch (e: unknown) {
      const msg = (e as Error).message;
      if (msg.toLowerCase().includes('unauthorized')) { onExpired(); return; }
      setErr(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleSetPrimary = async (m: ControllerMedia) => {
    try {
      await updateControllerMedia(product.id, m.id, { is_primary: true });
      onChanged();
    } catch (e: unknown) {
      const msg = (e as Error).message;
      if (msg.toLowerCase().includes('unauthorized')) { onExpired(); return; }
      setErr(msg);
    }
  };

  const handleDelete = async (m: ControllerMedia) => {
    if (!window.confirm(`Delete this photo? This cannot be undone.`)) return;
    try {
      await deleteControllerMedia(product.id, m.id);
      onChanged();
    } catch (e: unknown) {
      const msg = (e as Error).message;
      if (msg.toLowerCase().includes('unauthorized')) { onExpired(); return; }
      setErr(msg);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-bold uppercase tracking-widest text-[#B0B0B0]">Photos</p>
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-1.5 text-xs bg-[#39FF14]/20 hover:bg-[#39FF14]/30 text-[#39FF14] px-3 py-1.5 rounded-lg font-semibold transition-all disabled:opacity-50"
        >
          <Icon.Upload />
          {uploading ? `Uploading ${uploadPct}%…` : 'Upload Photo'}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={e => { const f = e.target.files?.[0]; if (f) handleUpload(f); e.target.value = ''; }}
        />
      </div>

      {err && <p className="text-red-400 text-xs mb-2">{err}</p>}

      {media.length === 0 && (
        <div className="border-2 border-dashed border-gray-700 rounded-xl p-6 text-center">
          <p className="text-[#B0B0B0] text-sm">No photos yet. Upload one above.</p>
        </div>
      )}

      <div className="grid grid-cols-3 gap-2">
        {media.map(m => (
          <div key={m.id} className="relative group rounded-xl overflow-hidden border border-gray-700 aspect-square bg-gray-900">
            <img src={m.public_url} alt={m.caption || 'media'} className="w-full h-full object-cover" />

            {m.is_primary && (
              <div className="absolute top-1.5 left-1.5 bg-[#39FF14] text-black rounded px-1.5 py-0.5 text-[10px] font-bold flex items-center gap-0.5">
                <Icon.Star />PRIMARY
              </div>
            )}

            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
              {!m.is_primary && (
                <button
                  onClick={() => handleSetPrimary(m)}
                  className="bg-[#39FF14]/20 hover:bg-[#39FF14]/40 text-[#39FF14] p-1.5 rounded-lg transition-all"
                  title="Set as primary"
                >
                  <Icon.Star />
                </button>
              )}
              <button
                onClick={() => handleDelete(m)}
                className="bg-red-500/20 hover:bg-red-500/40 text-red-400 p-1.5 rounded-lg transition-all"
                title="Delete"
              >
                <Icon.Trash />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Controller Editor Drawer ───────────────────────────────────────────────────
type EditorMode = 'edit' | 'new';

interface EditorProps {
  product: ControllerProduct | null;
  mode: EditorMode;
  onClose: () => void;
  onSaved: () => void;
  onExpired: () => void;
}

const BLANK: Partial<ControllerProduct> = {
  name: '', short_description: '', description: '',
  price: 175, compare_at_price: null,
  status: 'active', gallery_type: 'custom',
  featured: false, availability: 'available', sort_order: 0,
};

function ControllerEditorDrawer({ product, mode, onClose, onSaved, onExpired }: EditorProps) {
  const [fields, setFields] = useState<Partial<ControllerProduct>>(
    mode === 'new' ? { ...BLANK } : { ...product }
  );
  const [saving, setSaving]   = useState(false);
  const [err,    setErr]      = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const set = (k: keyof ControllerProduct, v: unknown) =>
    setFields(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!fields.name?.trim()) { setErr('Name is required'); return; }
    setSaving(true); setErr(null);
    try {
      if (mode === 'new') {
        await createController(fields);
      } else {
        await updateController(product!.id, fields);
      }
      onSaved();
    } catch (e: unknown) {
      const msg = (e as Error).message;
      if (msg.toLowerCase().includes('unauthorized')) { onExpired(); return; }
      setErr(msg);
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-[#E0E0E0] focus:outline-none focus:border-[#39FF14]";
  const labelCls = "block text-xs font-semibold text-[#B0B0B0] uppercase tracking-wide mb-1";

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div
        className="w-full max-w-md bg-[#111] border-l border-gray-800 flex flex-col h-full overflow-hidden shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800 shrink-0">
          <h2 className="text-lg font-bold text-[#E0E0E0]">
            {mode === 'new' ? 'Add Controller' : 'Edit Controller'}
          </h2>
          <button onClick={onClose} className="text-[#B0B0B0] hover:text-white p-1">
            <Icon.X />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Identity */}
          <div className="space-y-3">
            <div>
              <label className={labelCls}>Name *</label>
              <input className={inputCls} value={fields.name ?? ''} onChange={e => set('name', e.target.value)} placeholder="e.g. Blush Pink" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Gallery Type</label>
                <select className={inputCls} value={fields.gallery_type} onChange={e => set('gallery_type', e.target.value)}>
                  <option value="custom">Custom Graphics</option>
                  <option value="standard">Standard Colors</option>
                  <option value="edge">DualSense Edge</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Status</label>
                <select className={inputCls} value={fields.status} onChange={e => set('status', e.target.value)}>
                  <option value="active">Active</option>
                  <option value="draft">Draft</option>
                  <option value="hidden">Hidden</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Availability</label>
                <select className={inputCls} value={fields.availability} onChange={e => set('availability', e.target.value)}>
                  <option value="available">Available</option>
                  <option value="sold">Sold Out</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Sort Order</label>
                <input className={inputCls} type="number" value={fields.sort_order ?? 0} onChange={e => set('sort_order', Number(e.target.value))} />
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-[#B0B0B0] mb-3">Pricing</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Price ($) *</label>
                <input className={inputCls} type="number" min={0} value={fields.price ?? 0} onChange={e => set('price', Number(e.target.value))} />
              </div>
              <div>
                <label className={labelCls}>Compare At ($)</label>
                <input
                  className={inputCls} type="number" min={0}
                  value={fields.compare_at_price ?? ''}
                  placeholder="—"
                  onChange={e => set('compare_at_price', e.target.value ? Number(e.target.value) : null)}
                />
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-3">
            <div>
              <label className={labelCls}>Short Description</label>
              <input className={inputCls} value={fields.short_description ?? ''} onChange={e => set('short_description', e.target.value)} placeholder="One-line tagline" />
            </div>
            <div>
              <label className={labelCls}>Description</label>
              <textarea
                className={`${inputCls} resize-y`} rows={3}
                value={fields.description ?? ''}
                onChange={e => set('description', e.target.value)}
                placeholder="Longer product description"
              />
            </div>
          </div>

          {/* Media — only available after product exists */}
          {mode === 'edit' && product && (
            <div key={refreshKey}>
              <div className="border-t border-gray-800 pt-4">
                <MediaManager
                  product={{ ...product, ...(fields as ControllerProduct) }}
                  onChanged={() => { setRefreshKey(k => k + 1); onSaved(); }}
                  onExpired={onExpired}
                />
              </div>
            </div>
          )}

          {mode === 'new' && (
            <p className="text-xs text-[#B0B0B0] bg-gray-900 rounded-lg p-3">
              Save this controller first, then reopen it to upload photos.
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="shrink-0 px-5 py-4 border-t border-gray-800 space-y-2">
          {err && <p className="text-red-400 text-xs">{err}</p>}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-2.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-[#E0E0E0] text-sm font-semibold transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 py-2.5 rounded-lg bg-[#39FF14] hover:bg-[#00BFFF] text-black text-sm font-bold transition-all disabled:opacity-60"
            >
              {saving ? 'Saving…' : mode === 'new' ? 'Create' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Confirm Delete dialog ─────────────────────────────────────────────────────
function ConfirmDeleteDialog({ name, onConfirm, onCancel }: { name: string; onConfirm: () => void; onCancel: () => void }) {
  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onCancel}>
      <div className="bg-[#111] border border-gray-700 rounded-2xl p-6 w-full max-w-sm shadow-2xl" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-[#E0E0E0] mb-2">Delete controller?</h3>
        <p className="text-[#B0B0B0] text-sm mb-5">
          "{name}" and all its photos will be permanently deleted. This cannot be undone.
        </p>
        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 py-2.5 rounded-lg bg-gray-800 text-[#E0E0E0] text-sm font-semibold hover:bg-gray-700">Cancel</button>
          <button onClick={onConfirm} className="flex-1 py-2.5 rounded-lg bg-red-600 hover:bg-red-500 text-white text-sm font-bold">Delete</button>
        </div>
      </div>
    </div>
  );
}

// ── Bulk Pricing View ─────────────────────────────────────────────────────────
function BulkPricingView({
  products,
  onSaved,
  onExpired,
}: {
  products: ControllerProduct[];
  onSaved: () => void;
  onExpired: () => void;
}) {
  type Row = { id: string; name: string; gallery_type: string; price: number; compare_at_price: number | null };
  const [rows, setRows] = useState<Row[]>(() =>
    products.map(p => ({ id: p.id, name: p.name, gallery_type: p.gallery_type, price: p.price, compare_at_price: p.compare_at_price }))
  );
  const [saving, setSaving]   = useState(false);
  const [saved,  setSaved]    = useState(false);
  const [err,    setErr]      = useState<string | null>(null);
  const [batch,  setBatch]    = useState({ amount: '', type: '$' as '$' | '%', filter: 'all' as 'all' | 'custom' | 'standard' | 'edge' });

  const setPrice = (id: string, val: string) =>
    setRows(r => r.map(row => row.id === id ? { ...row, price: Number(val) || 0 } : row));
  const setCompare = (id: string, val: string) =>
    setRows(r => r.map(row => row.id === id ? { ...row, compare_at_price: val ? Number(val) : null } : row));

  const applyBatch = () => {
    const amt = parseFloat(batch.amount);
    if (isNaN(amt)) return;
    setRows(r => r.map(row => {
      if (batch.filter !== 'all' && row.gallery_type !== batch.filter) return row;
      const delta = batch.type === '$' ? amt : Math.round(row.price * amt / 100);
      return { ...row, price: Math.max(0, row.price + delta) };
    }));
    setBatch(b => ({ ...b, amount: '' }));
  };

  const handleSave = async () => {
    setSaving(true); setErr(null);
    try {
      await bulkUpdatePrices(rows.map(r => ({ id: r.id, price: r.price, compare_at_price: r.compare_at_price })));
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      onSaved();
    } catch (e: unknown) {
      const msg = (e as Error).message;
      if (msg.toLowerCase().includes('unauthorized')) { onExpired(); return; }
      setErr(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {/* Batch adjustment bar */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 mb-5 flex flex-wrap items-end gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-[#B0B0B0] mb-1">Batch Adjust</p>
          <div className="flex gap-2">
            <select
              className="bg-gray-800 border border-gray-700 rounded-lg px-2 py-1.5 text-xs text-[#E0E0E0]"
              value={batch.filter}
              onChange={e => setBatch(b => ({ ...b, filter: e.target.value as typeof b.filter }))}
            >
              <option value="all">All controllers</option>
              <option value="custom">Custom Graphics only</option>
              <option value="standard">Standard Colors only</option>
              <option value="edge">DualSense Edge only</option>
            </select>
            <div className="flex rounded-lg overflow-hidden border border-gray-700">
              {(['$','%'] as const).map(t => (
                <button
                  key={t}
                  onClick={() => setBatch(b => ({ ...b, type: t }))}
                  className={`px-3 py-1.5 text-xs font-bold transition-all ${batch.type === t ? 'bg-[#39FF14] text-black' : 'bg-gray-800 text-[#B0B0B0]'}`}
                >
                  {t}
                </button>
              ))}
            </div>
            <input
              type="number"
              placeholder="e.g. +10 or -5"
              value={batch.amount}
              onChange={e => setBatch(b => ({ ...b, amount: e.target.value }))}
              className="w-28 bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-xs text-[#E0E0E0]"
            />
            <button
              onClick={applyBatch}
              disabled={!batch.amount}
              className="px-4 py-1.5 rounded-lg bg-[#00BFFF]/20 hover:bg-[#00BFFF]/40 text-[#00BFFF] text-xs font-bold transition-all disabled:opacity-40"
            >
              Apply
            </button>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-3">
          {err && <span className="text-red-400 text-xs">{err}</span>}
          <button
            onClick={handleSave}
            disabled={saving}
            className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-bold transition-all disabled:opacity-60 ${saved ? 'bg-[#39FF14]/20 text-[#39FF14]' : 'bg-[#39FF14] hover:bg-[#00BFFF] text-black'}`}
          >
            {saved ? <><Icon.Check />Saved!</> : saving ? 'Saving…' : 'Save All Changes'}
          </button>
        </div>
      </div>

      {/* Price table */}
      <div className="overflow-x-auto rounded-xl border border-gray-800">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-800 bg-gray-900">
              <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-widest text-[#B0B0B0]">Controller</th>
              <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-widest text-[#B0B0B0]">Type</th>
              <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-widest text-[#B0B0B0]">Price</th>
              <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-widest text-[#B0B0B0]">Compare At</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={row.id} className={`border-b border-gray-800/50 ${i % 2 === 0 ? 'bg-[#0D0D0D]' : 'bg-gray-900/30'}`}>
                <td className="px-4 py-2.5 text-[#E0E0E0] font-medium">{row.name}</td>
                <td className="px-4 py-2.5">
                  <span className="text-xs text-[#B0B0B0]">{TYPE_LABEL[row.gallery_type] ?? row.gallery_type}</span>
                </td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-1">
                    <span className="text-[#B0B0B0] text-sm">$</span>
                    <input
                      type="number"
                      min={0}
                      value={row.price}
                      onChange={e => setPrice(row.id, e.target.value)}
                      className="w-20 bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-[#E0E0E0] focus:outline-none focus:border-[#39FF14]"
                    />
                  </div>
                </td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-1">
                    <span className="text-[#B0B0B0] text-sm">$</span>
                    <input
                      type="number"
                      min={0}
                      placeholder="—"
                      value={row.compare_at_price ?? ''}
                      onChange={e => setCompare(row.id, e.target.value)}
                      className="w-20 bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-[#E0E0E0] focus:outline-none focus:border-[#39FF14]"
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Catalog View ──────────────────────────────────────────────────────────────
type TypeFilter = 'all' | 'custom' | 'standard' | 'edge';

function CatalogView({
  products,
  onEdit,
  onAdd,
  onDelete,
  onReorder,
  onExpired,
}: {
  products: ControllerProduct[];
  onEdit:    (p: ControllerProduct) => void;
  onAdd:     () => void;
  onDelete:  (p: ControllerProduct) => void;
  onReorder: (orders: { id: string; sort_order: number }[]) => void;
  onExpired: () => void;
}) {
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');

  // Drag state
  const dragId   = useRef<string | null>(null);
  const dragType = useRef<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  const handleDragStart = (id: string, type: string) => {
    dragId.current   = id;
    dragType.current = type;
  };

  const handleDragOver = (e: DragEvent, id: string, type: string) => {
    if (dragType.current !== type || dragId.current === id) return;
    e.preventDefault();
    setDragOverId(id);
  };

  const handleDrop = (e: DragEvent, targetId: string, type: string) => {
    e.preventDefault();
    setDragOverId(null);
    if (!dragId.current || dragId.current === targetId || dragType.current !== type) return;

    const group = products
      .filter(p => p.gallery_type === type)
      .slice()
      .sort((a, b) => a.sort_order - b.sort_order);

    const fromIdx = group.findIndex(p => p.id === dragId.current);
    const toIdx   = group.findIndex(p => p.id === targetId);
    if (fromIdx === -1 || toIdx === -1) return;

    const reordered = [...group];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);

    onReorder(reordered.map((p, i) => ({ id: p.id, sort_order: i })));
    dragId.current   = null;
    dragType.current = null;
  };

  const handleDragEnd = () => {
    setDragOverId(null);
    dragId.current   = null;
    dragType.current = null;
  };

  const filtered = products.filter(p => typeFilter === 'all' || p.gallery_type === typeFilter);
  const byType   = (t: string) => filtered.filter(p => p.gallery_type === t);

  const renderGroup = (type: string) => {
    const group = byType(type);
    if (!group.length) return null;
    return (
      <div key={type} className="mb-10">
        <div className="flex items-center gap-3 mb-4">
          <h3 className="text-sm font-bold text-[#E0E0E0] uppercase tracking-widest">{TYPE_LABEL[type]}</h3>
          <span className="text-xs text-[#B0B0B0]">{group.length} items</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {group.map((p) => {
            const primaryImg = p.media?.find(m => m.is_primary)?.public_url ?? p.media?.[0]?.public_url;
            const isOver = dragOverId === p.id;
            return (
              <div
                key={p.id}
                draggable
                onDragStart={() => handleDragStart(p.id, p.gallery_type)}
                onDragOver={e => handleDragOver(e, p.id, p.gallery_type)}
                onDrop={e => handleDrop(e, p.id, p.gallery_type)}
                onDragEnd={handleDragEnd}
                className={`group bg-gray-900 rounded-xl border overflow-hidden transition-all cursor-grab active:cursor-grabbing select-none
                  ${isOver ? 'border-[#39FF14] ring-1 ring-[#39FF14]/40 scale-[1.02]' : 'border-gray-800 hover:border-gray-600'}`}
              >
                {/* Photo */}
                <div className="aspect-square bg-gray-800 relative overflow-hidden">
                  {primaryImg ? (
                    <img src={primaryImg} alt={p.name} className="w-full h-full object-cover pointer-events-none" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-600 text-xs">No photo</div>
                  )}
                  {/* Drag handle */}
                  <div className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 rounded p-1 text-[#B0B0B0]">
                    <Icon.Grip />
                  </div>
                  {/* Status badge */}
                  {p.status !== 'active' && (
                    <div className="absolute top-1.5 left-1.5">
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase ${STATUS_BADGE[p.status]}`}>
                        {p.status}
                      </span>
                    </div>
                  )}
                </div>
                {/* Info */}
                <div className="p-2.5">
                  <p className="text-xs font-semibold text-[#E0E0E0] truncate">{p.name}</p>
                  <p className="text-xs text-[#39FF14] font-bold mt-0.5">${p.price}</p>
                  {p.compare_at_price && (
                    <p className="text-[10px] text-[#B0B0B0] line-through">${p.compare_at_price}</p>
                  )}
                </div>
                {/* Actions */}
                <div className="flex border-t border-gray-800">
                  <button
                    onClick={() => onEdit(p)}
                    className="flex-1 flex items-center justify-center gap-1 py-2 text-xs text-[#00BFFF] hover:bg-gray-800 transition-all"
                  >
                    <Icon.Edit />Edit
                  </button>
                  <div className="w-px bg-gray-800" />
                  <button
                    onClick={() => onDelete(p)}
                    className="flex-1 flex items-center justify-center gap-1 py-2 text-xs text-red-400 hover:bg-gray-800 transition-all"
                  >
                    <Icon.Trash />Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Controls */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex bg-gray-900 border border-gray-800 rounded-xl p-1 gap-1">
          {([
            { key: 'all',      label: 'All' },
            { key: 'custom',   label: 'Custom' },
            { key: 'standard', label: 'Standard' },
            { key: 'edge',     label: 'Edge' },
          ] as const).map(tab => (
            <button
              key={tab.key}
              onClick={() => setTypeFilter(tab.key)}
              className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all ${typeFilter === tab.key ? 'bg-[#39FF14]/20 text-[#39FF14] border border-[#39FF14]/40' : 'text-[#B0B0B0]'}`}
            >
              {tab.label}
              {tab.key !== 'all' && (
                <span className="ml-1 text-[#B0B0B0]">
                  ({products.filter(p => p.gallery_type === tab.key).length})
                </span>
              )}
            </button>
          ))}
        </div>
        <button
          onClick={onAdd}
          className="flex items-center gap-2 bg-[#39FF14] hover:bg-[#00BFFF] text-black px-4 py-2 rounded-lg text-sm font-bold transition-all"
        >
          <Icon.Plus />Add Controller
        </button>
      </div>

      {/* Groups */}
      {typeFilter === 'all' ? (
        <>
          {renderGroup('custom')}
          {renderGroup('standard')}
          {renderGroup('edge')}
        </>
      ) : (
        renderGroup(typeFilter)
      )}

      {filtered.length === 0 && (
        <div className="text-center py-16 text-[#B0B0B0]">
          No controllers in this category yet.
        </div>
      )}
    </div>
  );
}

// ── Main page content ─────────────────────────────────────────────────────────
function AdminControllersContent({ onExpired }: { onExpired: () => void }) {
  const { products, loading, error, reload, setProducts } = useControllers(true);
  const [view, setView]   = useState<'catalog' | 'bulk-price'>('catalog');
  const [editor, setEditor] = useState<{ product: ControllerProduct | null; mode: 'edit' | 'new' } | null>(null);
  const [toDelete, setToDelete] = useState<ControllerProduct | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleEdit = (p: ControllerProduct) => setEditor({ product: p, mode: 'edit' });
  const handleAdd  = () => setEditor({ product: null, mode: 'new' });
  const handleClose = () => setEditor(null);

  const handleSaved = useCallback(() => {
    setEditor(null);
    reload();
  }, [reload]);

  const handleDeleteConfirm = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await deleteController(toDelete.id);
      setToDelete(null);
      reload();
    } catch (e: unknown) {
      const msg = (e as Error).message;
      if (msg.toLowerCase().includes('unauthorized')) { onExpired(); return; }
      alert('Delete failed: ' + msg);
    } finally {
      setDeleting(false);
    }
  };

  const handleReorder = useCallback(async (newOrders: { id: string; sort_order: number }[]) => {
    // Optimistic update
    setProducts(prev => prev.map(p => {
      const match = newOrders.find(o => o.id === p.id);
      return match ? { ...p, sort_order: match.sort_order } : p;
    }));
    try {
      await reorderControllers(newOrders);
    } catch (e: unknown) {
      reload();
      const msg = (e as Error).message;
      if (msg.toLowerCase().includes('unauthorized')) onExpired();
    }
  }, [setProducts, reload, onExpired]);

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-[#E0E0E0]">
      <SEOHead title="Admin: Controllers" noIndex />

      {/* Header */}
      <div className="bg-[#111] border-b border-gray-800 px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold">Controller Catalog</h1>
          <p className="text-xs text-[#B0B0B0] mt-0.5">{products.length} controllers</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={reload} className="text-[#B0B0B0] hover:text-[#E0E0E0] p-2 rounded-lg hover:bg-gray-800 transition-all" title="Refresh">
            <Icon.Refresh />
          </button>
          <a href="/custom-controllers" target="_blank" className="text-xs text-[#00BFFF] hover:underline">
            View public page ↗
          </a>
        </div>
      </div>

      {/* Tab nav */}
      <div className="bg-[#111] border-b border-gray-800 px-6">
        <div className="flex gap-0">
          {([
            { key: 'catalog',    label: 'Catalog' },
            { key: 'bulk-price', label: 'Bulk Pricing' },
          ] as const).map(tab => (
            <button
              key={tab.key}
              onClick={() => setView(tab.key)}
              className={`px-5 py-3 text-sm font-semibold border-b-2 transition-all ${view === tab.key ? 'border-[#39FF14] text-[#39FF14]' : 'border-transparent text-[#B0B0B0] hover:text-[#E0E0E0]'}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {loading && <p className="text-[#B0B0B0] text-center py-16">Loading…</p>}
        {error   && <p className="text-red-400 text-center py-8">Error: {error}</p>}

        {!loading && !error && view === 'catalog' && (
          <CatalogView
            products={[...products].sort((a, b) => a.sort_order - b.sort_order)}
            onEdit={handleEdit}
            onAdd={handleAdd}
            onDelete={p => setToDelete(p)}
            onReorder={handleReorder}
            onExpired={onExpired}
          />
        )}

        {!loading && !error && view === 'bulk-price' && (
          <BulkPricingView
            products={[...products].sort((a, b) => {
              const typeOrder = ['custom', 'standard', 'edge'];
              const ta = typeOrder.indexOf(a.gallery_type);
              const tb = typeOrder.indexOf(b.gallery_type);
              return ta !== tb ? ta - tb : a.sort_order - b.sort_order;
            })}
            onSaved={reload}
            onExpired={onExpired}
          />
        )}
      </div>

      {/* Editor drawer */}
      {editor && (
        <ControllerEditorDrawer
          product={editor.mode === 'edit'
            ? products.find(p => p.id === editor.product?.id) ?? editor.product
            : null}
          mode={editor.mode}
          onClose={handleClose}
          onSaved={handleSaved}
          onExpired={onExpired}
        />
      )}

      {/* Delete confirmation */}
      {toDelete && (
        <ConfirmDeleteDialog
          name={toDelete.name}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setToDelete(null)}
        />
      )}

      {deleting && (
        <div className="fixed inset-0 z-70 flex items-center justify-center bg-black/60">
          <p className="text-[#E0E0E0]">Deleting…</p>
        </div>
      )}
    </div>
  );
}

// ── Page entry point ──────────────────────────────────────────────────────────
export default function AdminControllersPage() {
  const [authed, setAuthed] = useState(isAdminAuthed());

  const handleExpired = useCallback(() => {
    clearAdminAuth();
    setAuthed(false);
  }, []);

  return authed
    ? <AdminControllersContent onExpired={handleExpired} />
    : <AdminLoginGate onAuth={() => setAuthed(true)} />;
}
